import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ImportService } from '../../shared/services/import/import.service';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { ProjectEventsService } from '../../shared/services/project-events/project-events.service';
import { ImportBody } from '@app/shared/interfaces';
import { ZipReader, BlobReader, TextWriter, Entry } from '@zip.js/zip.js';

type ZipTextEntry = Entry & {
  getData: (writer: TextWriter) => Promise<string>;
};

enum ImportStage {
  FileSelection = 'file_selection',
  Analyzing = 'analyzing',
  ProjectDetails = 'project_details',
  Importing = 'importing',
  Complete = 'complete'
}

interface ExportVersion {
  // Handle different possible property names
  version_id?: number;
  versionId?: number;
  id?: number;

  major_version?: number;
  majorVersion?: number;

  minor_version?: number;
  minorVersion?: number;

  folder_path?: string;
  folderPath?: string;

  created_at?: string;
  createdAt?: string;

  notebooks?: any[];
  default_state?: string;
  defaultState?: string;
  default_workflow_id?: number;
  defaultWorkflowId?: number;

  // Allow any additional properties
  [key: string]: any;
}

@Component({
  selector: 'app-import-project-dialog',
  templateUrl: './import-project-dialog.component.html',
  styleUrls: ['./import-project-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule
  ]
})
export class ImportProjectDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<ImportProjectDialogComponent>>(MatDialogRef);
  private fb = inject(FormBuilder);
  private importService = inject(ImportService);
  private appStateService = inject(AppStateService);
  private readonly projectEvents = inject(ProjectEventsService);

  currentStage = signal<ImportStage>(ImportStage.FileSelection);
  selectedFile: File | null = null;
  uploadProgress: number = 0;
  importForm: FormGroup;
  hidePassword: boolean = true;
  error = signal<string | null>(null);
  availableVersions = signal<ExportVersion[]>([]);
  selectedVersionId = signal<number | null>(null);
  isAnalyzing = signal(false);
  warnings = signal<string[]>([]);

  // Expose enum to template
  ImportStage = ImportStage;

  constructor() {
    this.importForm = this.fb.group({
      password: ['', Validators.required],
      projectName: ['', [Validators.required, this.uniqueProjectNameValidator.bind(this)]]
    });
  }

  get dialogTitle(): string {
    switch (this.currentStage()) {
      case ImportStage.FileSelection:
        return 'Import Project (1/3)';
      case ImportStage.Analyzing:
        return 'Import Project (2/3)';
      case ImportStage.ProjectDetails:
        return 'Import Project (3/3)';
      case ImportStage.Importing:
        return 'Importing...';
      case ImportStage.Complete:
        return 'Import Complete';
      default:
        return 'Import Project';
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      // Pre-fill project name with file name (without extension)
      const fileName = this.selectedFile.name;
      const projectName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
      this.importForm.patchValue({ projectName });

      // Reset version data
      this.availableVersions.set([]);
      this.selectedVersionId.set(null);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.selectedFile = event.dataTransfer.files[0];
      // Pre-fill project name with file name (without extension)
      const fileName = this.selectedFile.name;
      const projectName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
      this.importForm.patchValue({ projectName });

      // Reset version data
      this.availableVersions.set([]);
      this.selectedVersionId.set(null);
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    this.availableVersions.set([]);
    this.selectedVersionId.set(null);
  }

  async uploadAndAnalyze(): Promise<void> {
    if (!this.selectedFile) return;

    this.currentStage.set(ImportStage.Analyzing);
    this.error.set(null);
    this.isAnalyzing.set(true);

    try {
      const versions = await this.extractVersionsFromZip(this.selectedFile);
      this.availableVersions.set(versions);
      this.selectedVersionId.set(this.getVersionId(this.getLatestVersion(versions)));
      this.currentStage.set(ImportStage.ProjectDetails);
    } catch (error) {
      this.handleAnalysisError(error);
    } finally {
      this.isAnalyzing.set(false);
    }
  }

  /**
   * Extract versions from ZIP file
   */
  private async extractVersionsFromZip(file: File): Promise<ExportVersion[]> {
    const zipReader = new ZipReader(new BlobReader(file));
    
    try {
      const entries = await zipReader.getEntries();
      const versionsText = await this.readVersionsFile(entries);
      const versions = this.parseVersions(versionsText);
      
      await zipReader.close();
      return versions;
    } catch (error) {
      await zipReader.close().catch(() => {}); // Ensure cleanup even on error
      throw error;
    }
  }

  /**
   * Read versions.json from ZIP entries
   */
  private async readVersionsFile(entries: Entry[]): Promise<string> {
    const versionsEntry = entries.find((entry): entry is ZipTextEntry => {
      return entry.filename === 'versions.json' && 'getData' in entry;
    });
    if (!versionsEntry) {
      throw new Error('Invalid export file: versions.json not found');
    }

    const textWriter = new TextWriter();
    const versionsText = await versionsEntry.getData(textWriter);
    return versionsText;
  }

  /**
   * Parse versions JSON text into ExportVersion array
   */
  private parseVersions(versionsText: string): ExportVersion[] {
    const versions: ExportVersion[] = JSON.parse(versionsText);
    
    console.log('Parsed versions:', versions);
    
    if (!versions || versions.length === 0) {
      throw new Error('No versions found in export file');
    }
    
    return versions;
  }

  /**
   * Handle errors during ZIP analysis
   */
  private handleAnalysisError(error: unknown): void {
    console.error('Error analyzing export file:', error);
    this.error.set(error instanceof Error
      ? error.message
      : 'Failed to analyze export file. Please ensure it is a valid project export.');
    this.currentStage.set(ImportStage.FileSelection);
  }

  public getVersionId(version: ExportVersion): number {
    return version.version_id || version.versionId || version.id || 0;
  }

  public getMajorVersion(version: ExportVersion): number {
    return version.major_version || version.majorVersion || 0;
  }

  public getMinorVersion(version: ExportVersion): number {
    return version.minor_version || version.minorVersion || 0;
  }

  public getCreatedAt(version: ExportVersion): string {
    return version.created_at || version.createdAt || '';
  }

  private getLatestVersion(versions: ExportVersion[]): ExportVersion {
    // Sort by version_id descending to get the latest
    return versions.sort((a, b) => this.getVersionId(b) - this.getVersionId(a))[0];
  }

  getVersionDisplayName(version: ExportVersion): string {
    const versionId = this.getVersionId(version);
    const majorVersion = this.getMajorVersion(version);
    const minorVersion = this.getMinorVersion(version);

    return `v${majorVersion}.${minorVersion} (ID: ${versionId})`;
  }

  onVersionChange(versionId: number): void {
    this.selectedVersionId.set(versionId);
  }

  confirmImport(): void {
    if (this.importForm.invalid || !this.selectedFile || !this.selectedVersionId()) return;

    this.currentStage.set(ImportStage.Importing);
    this.error.set(null);

    const importData: ImportBody = {
      file: this.selectedFile,
      json: {
        projectName: this.importForm.value.projectName,
        versionId: this.selectedVersionId()!,
        password: this.importForm.value.password
      }
    };

    this.importService.importProject(importData).subscribe({
      next: (response) => {
        const importWarnings = (response as any).warnings || [];
        this.warnings.set(importWarnings);

        // Notify the projects list to refresh
        this.projectEvents.notifyCreated((response as any).projectId);

        if (importWarnings.length > 0) {
          this.currentStage.set(ImportStage.Complete);
        } else {
          this.dialogRef.close(response);
        }
      },
      error: (err) => {
        this.error.set(err.error?.message || 'Failed to import project. Please try again.');
        this.currentStage.set(ImportStage.ProjectDetails);
      }
    });
  }

  copyWarnings(): void {
    const warningText = this.warnings().join('\n');
    navigator.clipboard.writeText(warningText);
  }

  private uniqueProjectNameValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const trimmedName = control.value.trim().toLowerCase();
    const exists = this.appStateService.projects().some(
      (p) => p.name.trim().toLowerCase() === trimmedName,
    );
    return exists ? { notUnique: true } : null;
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
